import { gql } from "@apollo/client";
import client from "../apolloClient";
import {
  ClonePlaylistPayload,
  PlaylistFilters,
  PlaylistFormData,
  PlaylistResult,
  UpdatePlaylistPayload,
} from "../../schemas/playlistSchema";

const GET_PLAYLISTS = gql`
  query GetPlaylists(
    $page: Int
    $pageSize: Int
    $search: String
    $type: String
    $tagIds: [ID!]
  ) {
    playlists(
      page: $page
      pageSize: $pageSize
      search: $search
      type: $type
      tagIds: $tagIds
    ) {
      playlists {
        id
        name
        type
        duration
        isActive
        tenantId
        createdBy
        createdAt
        updatedAt
        tags
        tagIds
        isClientPlaylist
        screenId
        creatives {
          id
          uniqueId
        }
        subPlaylists {
          id
          name
          type
        }
      }
      page
      pageSize
      total
      totalPages
      hasNextPage
      hasPrevPage
    }
  }
`;

const GET_PLAYLIST_BY_ID = gql`
  query GetPlaylist($id: ID!) {
    playlist(id: $id) {
      id
      name
      type
      duration
      isActive
      tenantId
      createdBy
      createdAt
      updatedAt
      tags
      tagIds
      isClientPlaylist
      screenId
      creatives {
        id
        uniqueId
      }
      subPlaylists {
        id
        name
        type
      }
    }
  }
`;

const GET_PLAYLIST_ASSIGNMENTS = gql`
  query GetPlaylistAssignments($playlistId: ID!) {
    playlistAssignments(playlistId: $playlistId) {
      id
      playlistId
      itemId
      repetitionCount
      playOrder
      name
      fileUrl
      thumbnailUrl
      duration
      isPaused
    }
  }
`;

const CREATE_PLAYLIST = gql`
  mutation CreatePlaylist($input: CreatePlaylistInput!) {
    createPlaylist(input: $input)
  }
`;

const UPDATE_PLAYLIST = gql`
  mutation UpdatePlaylist($id: ID!, $input: UpdatePlaylistInput!) {
    updatePlaylist(id: $id, input: $input)
  }
`;

const DELETE_PLAYLISTS = gql`
  mutation DeletePlaylists($ids: [ID!]!) {
    deletePlaylists(ids: $ids)
  }
`;

const CLONE_PLAYLIST = gql`
  mutation ClonePlaylist($id: ID!, $name: String!) {
    clonePlaylist(id: $id, name: $name)
  }
`;

const ADD_CREATIVE_TO_SUB_PLAYLIST = gql`
  mutation AddCreativeToSubPlaylist(
    $subPlaylistId: ID!
    $creativeIds: [ID!]!
    $startPlayOrder: Int
  ) {
    addCreativeToSubPlaylist(
      subPlaylistId: $subPlaylistId
      creativeIds: $creativeIds
      startPlayOrder: $startPlayOrder
    )
  }
`;

const ADD_SUB_PLAYLISTS_TO_MAIN_PLAYLIST = gql`
  mutation AddSubPlaylistsToMainPlaylist(
    $mainPlaylistId: ID!
    $subPlaylistIds: [ID!]!
    $startPlayOrder: Int
  ) {
    addSubPlaylistsToMainPlaylist(
      mainPlaylistId: $mainPlaylistId
      subPlaylistIds: $subPlaylistIds
      startPlayOrder: $startPlayOrder
    )
  }
`;

const UPDATE_ASSIGNMENT_COUNT = gql`
  mutation UpdateAssignmentCount($assignmentId: ID!, $count: Int!) {
    updateAssignmentCount(assignmentId: $assignmentId, count: $count)
  }
`;

const UPDATE_PLAY_ORDER = gql`
  mutation UpdatePlayOrder($assignmentId: ID!, $playOrder: Int!) {
    updatePlayOrder(assignmentId: $assignmentId, playOrder: $playOrder)
  }
`;

const REORDER_PLAYLIST_ITEMS = gql`
  mutation ReorderPlaylistItems($playlistId: ID!, $assignmentIds: [ID!]!) {
    reorderPlaylistItems(playlistId: $playlistId, assignmentIds: $assignmentIds)
  }
`;

const REMOVE_ASSIGNMENT = gql`
  mutation RemoveAssignment($assignmentId: ID!) {
    removeAssignment(assignmentId: $assignmentId)
  }
`;

export const playlistService = {
  getAll: async (filters: PlaylistFilters = {}): Promise<PlaylistResult> => {
    const variables: PlaylistFilters = { ...filters };

    // Backward compatibility: older filter form provides single "tag".
    // Backend query filters by tagIds, so map it when needed.
    if ((!variables.tagIds || variables.tagIds.length === 0) && variables.tag) {
      variables.tagIds = [variables.tag];
    }

    const { data } = await client.query({
      query: GET_PLAYLISTS,
      variables,
      fetchPolicy: "network-only",
    });
    return data.playlists;
  },

  getById: async (id: string) => {
    const { data } = await client.query({
      query: GET_PLAYLIST_BY_ID,
      variables: { id },
      fetchPolicy: "network-only",
    });
    return data.playlist;
  },

  create: async (input: PlaylistFormData): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: CREATE_PLAYLIST,
      variables: {
        input: {
          name: input.name,
          type: input.type,
          isActive: input.isActive ?? true,
          tagIds: input.tagIds,
        },
      },
    });
    return data.createPlaylist;
  },

  update: async (payload: UpdatePlaylistPayload): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: UPDATE_PLAYLIST,
      variables: {
        id: payload.id,
        input: {
          name: payload.playlist.name,
          type: payload.playlist.type,
          isActive: payload.playlist.isActive,
          tagIds: payload.playlist.tagIds,
        },
      },
    });
    return data.updatePlaylist;
  },

  remove: async (ids: string[]): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: DELETE_PLAYLISTS,
      variables: { ids },
    });
    return data.deletePlaylists;
  },

  clone: async (input: ClonePlaylistPayload): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: CLONE_PLAYLIST,
      variables: {
        id: input.id,
        name: input.name,
      },
    });
    return data.clonePlaylist;
  },

  getPlaylistItems: async (playlistId: string) => {
    const { data } = await client.query({
      query: GET_PLAYLIST_ASSIGNMENTS,
      variables: { playlistId },
      fetchPolicy: "network-only",
    });
    return data.playlistAssignments;
  },

  removePlaylistItem: async (id: string): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: REMOVE_ASSIGNMENT,
      variables: { assignmentId: id },
    });
    return data.removeAssignment;
  },

  /**
   * `startPlayOrder` is the 1-based slot the first creative should take; the
   * backend shifts existing items down to make room. Omit it to append.
   */
  addCreativeToSubPlaylist: async (
    subPlaylistId: string,
    creativeIds: string[],
    startPlayOrder?: number,
  ): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: ADD_CREATIVE_TO_SUB_PLAYLIST,
      variables: {
        subPlaylistId,
        creativeIds,
        startPlayOrder,
      },
    });
    return data.addCreativeToSubPlaylist;
  },

  /**
   * `startPlayOrder` is the 1-based slot the first sub playlist should take; the
   * backend shifts existing items down to make room. Omit it to append.
   */
  addSubPlaylistsToMainPlaylist: async (
    mainPlaylistId: string,
    subPlaylistIds: string[],
    startPlayOrder?: number,
  ): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: ADD_SUB_PLAYLISTS_TO_MAIN_PLAYLIST,
      variables: {
        mainPlaylistId,
        subPlaylistIds,
        startPlayOrder,
      },
    });
    return data.addSubPlaylistsToMainPlaylist;
  },

  updateAssignmentCount: async (
    assignmentId: string,
    count: number,
  ): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: UPDATE_ASSIGNMENT_COUNT,
      variables: {
        assignmentId,
        count,
      },
    });
    return data.updateAssignmentCount;
  },

  updatePlayOrder: async (
    assignmentId: string,
    playOrder: number,
  ): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: UPDATE_PLAY_ORDER,
      variables: {
        assignmentId,
        playOrder,
      },
    });
    return data.updatePlayOrder;
  },

  reorderPlaylistItems: async (
    playlistId: string,
    assignmentIds: string[],
  ): Promise<boolean> => {
    const { data } = await client.mutate({
      mutation: REORDER_PLAYLIST_ITEMS,
      variables: {
        playlistId,
        assignmentIds,
      },
    });
    return data.reorderPlaylistItems;
  },
};
